// ─── Module inventory — liste des inventaires de l'espace ─────────
// Équivalent de l'écran « Inventaire / Stock » de MoPCal, aux mêmes conventions :
// ＋ en haut à droite, feuille de création/édition à 82 % avec 💾 grisée tant
// qu'il n'y a rien à enregistrer, swipe ← supprimer / → éditer, appui long en
// repli sur le web.
//
// `mode` : 'edit' (My Inventory) ou 'readonly' (MoPCal, qui consulte sans jamais
// toucher à la structure ni aux quantités).
import React, { useEffect, useMemo, useState } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, ScrollView, Modal, StyleSheet, ActivityIndicator, Platform,
} from 'react-native';
import {
  useUI, useAuth, useEspace, PageHeader, EmptyState, LigneSwipe, SelecteurEmoji, Feuille,
  confirmerSuppression, useT, useQuotaElements, useResetOnglet, usePrendreJeton, messageJetons,
} from '@mop/core';
import {
  ecouterInventairesAccessibles, creerInventaire, renommerInventaire, modifierEmojiInventaire,
  supprimerInventaire, aplatirArbre,
  ecouterModelesInventaire, supprimerModeleInventaire,
} from './services/inventaires';
import { MODELES_INVENTAIRE, instancierModele, nbCategoriesModele, labelTemplate, descTemplate } from './data/templates';
import { messageErreur } from './services/erreurs';
import EcranInventaire from './EcranInventaire';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

export default function EcranInventaires({ mode = 'edit', ongletCle = 'Inventaires' }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { user } = useAuth();
  const { tenantId, tenantIds, tenantRangement, espaceActif, assurerEspace } = useEspace();
  const prendreJeton = usePrendreJeton();
  const edit = mode === 'edit';
  // Creer = POSSEDER un element de plus, tous types confondus (calendriers
  // compris). Meme reponse que dans My Calendar : une seule source de verite.
  const quota = useQuotaElements();

  const [inventaires, setInv] = useState([]);
  const [mesModeles, setMesModeles] = useState([]);   // modèles de structure enregistrés
  const [charge, setCharge]   = useState(false);
  const [ouvert, setOuvert]   = useState(null);   // inventaire ouvert (détail)
  const [err, setErr]         = useState('');

  // Feuille de création / édition : { id|null, nom, emoji, modele }
  const [sheet, setSheet] = useState(null);
  const [busy, setBusy]   = useState(false);

  // Meme regle que partout : toucher l'onglet deja actif referme ce qui est
  // ouvert par-dessus et ramene au niveau 0 (la liste).
  useResetOnglet(ongletCle, () => { setOuvert(null); setSheet(null); });

  useEffect(() => {
    if (!user?.uid) { setInv([]); setCharge(true); return undefined; }
    setCharge(false);
    // Modèle propriétaire/accès : tous les inventaires où j'ai un accès (`acces`),
    // à travers TOUS les espaces (créateur, propriétaire d'espace, lecteur ou
    // rédacteur). Le filtrage est fait par la requête — plus d'espace actif.
    return ecouterInventairesAccessibles(
      user.uid,
      (l) => { setInv(l); setCharge(true); },
      (e) => { setCharge(true); setErr(messageErreur(e)); },
    );
  }, [user?.uid]);

  // Modèles de structure enregistrés par l'utilisateur (réutilisables à la création).
  useEffect(() => {
    if (!tenantId) { setMesModeles([]); return undefined; }
    return ecouterModelesInventaire(tenantIds.length ? tenantIds : tenantId, setMesModeles, () => setMesModeles([]));
  }, [tenantId, tenantIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  function supprimerModele(m) {
    confirmerSuppression(t('inventaire.supprModeleTitre'), t('inventaire.supprModeleMsg', { nom: m.nom }),
      async () => { try { await supprimerModeleInventaire(m.id); } catch (e) { setErr(messageErreur(e)); } });
  }

  // 💾 active seulement s'il y a vraiment quelque chose à enregistrer.
  const modifie = !!sheet && (
    sheet.id
      ? (sheet.nom.trim() !== sheet.nomInitial || sheet.emoji !== sheet.emojiInitial) && !!sheet.nom.trim()
      : !!(sheet.nom.trim() || sheet.modele)
  );

  async function enregistrer() {
    if (!sheet || !modifie || busy) return;
    // Masquer le « ＋ » ne suffit pas : une feuille restée ouverte, ou un élément
    // créé entre-temps sur un autre appareil, passerait au travers.
    if (!sheet.id && !quota.peutCreer) {
      setErr(t('jetons.creationBloquee', { n: quota.possedes, max: quota.max }));
      return;
    }
    setBusy(true); setErr('');
    try {
      if (sheet.id) {
        if (sheet.nom.trim() !== sheet.nomInitial) await renommerInventaire(sheet.id, sheet.nom.trim());
        if (sheet.emoji !== sheet.emojiInitial)        await modifierEmojiInventaire(sheet.id, sheet.emoji);
      } else {
        const m = sheet.modele ? instancierModele(sheet.modele) : null;
        // Un inventaire a besoin d'un espace : on le cree au nom de
        // l'utilisateur s'il n'en possede aucun (meme regle que les calendriers).
        const espaceId = tenantId || await assurerEspace();
        const idCree = await creerInventaire({
          tenantId: espaceId,
          // Si l'espace a adopté un calendrier MoPCal, le nouvel inventaire y est
          // rattaché aussi → MoPCal le voit tout de suite.
          tenantHerite: tenantRangement,
          nom: sheet.nom.trim() || (m ? m.nom : t('inventaire.titreDefaut')),
          emoji: sheet.emoji || (m ? m.emoji : '📦'),
          arbre: m ? m.arbre : [],
          proprietes: m ? m.proprietes : [],
          creePar: user?.uid,
          creeParNom: user?.prenom || user?.email || '',
          ownerUid: espaceActif?.creePar || user?.uid,
        });
        // Ce qu'on vient de creer, on le GERE : il entre dans la file avec un
        // jeton ⚙️. Si le serveur refuse, l'erreur remonte (l'inventaire existe
        // mais ne s'affichera pas tant que rien n'est libere).
        if (idCree) await prendreJeton(`inventaire:${idCree}`);
      }
      setSheet(null);
    } catch (e) { setErr(messageErreur(e)); }
    setBusy(false);
  }

  function supprimer(inv) {
    confirmerSuppression(
      t('inventaire.supprInvTitre'),
      t('inventaire.supprInvMsg', { titre: inv.nom }),
      async () => { try { await supprimerInventaire(inv.id); } catch (e) { setErr(messageErreur(e)); } },
    );
  }

  const ouvrirCreation = () => setSheet({ id: null, nom: '', emoji: '📦', modele: null, nomInitial: '', emojiInitial: '📦' });
  const ouvrirEdition  = (inv) => setSheet({
    id: inv.id, nom: inv.nom || '', emoji: inv.emoji || '📦', modele: null,
    nomInitial: inv.nom || '', emojiInitial: inv.emoji || '📦',
  });

  // Détail plein écran : le module gère sa propre pile, l'app n'a rien à câbler.
  if (ouvert) {
    return <EcranInventaire inventaireId={ouvert.id} mode={mode} ongletCle={ongletCle} onRetour={() => setOuvert(null)} />;
  }

  // Un MEMBRE (sans espace possédé) accède quand même aux inventaires PARTAGÉS
  // avec lui (liste par `acces`). Seule la CRÉATION exige un espace (tenantId).
  return (
    <View style={s.page}>
      <PageHeader
        titre={t('inventaire.titrePluriel')} iconeTitre="📦"
        action={edit && quota.peutCreer ? { icone: '＋', onPress: ouvrirCreation, label: t('inventaire.nouvelInventaire') } : null}
      />

      {!!err && (
        <TouchableOpacity style={s.err} onPress={() => setErr('')}>
          <Text style={s.errTxt}>⚠️ {err}</Text>
          <Text style={s.errAide}>{t('commun.toucherMasquer')}</Text>
        </TouchableOpacity>
      )}

      <ScrollView contentContainerStyle={s.corps}>
        {!!espaceActif && <Text style={s.espace}>{espaceActif.emoji || '🏠'} {espaceActif.nom}</Text>}
        {edit && !!tenantId && quota.atteint && quota.fiable && (
          <Text style={s.quota}>{t('jetons.creationBloquee', { n: quota.possedes, max: quota.max })}</Text>
        )}

        {!charge && <ActivityIndicator color={UI.accent} style={{ marginTop: 24 }} />}

        {charge && inventaires.length === 0 && (
          <EmptyState icon="📦" texte={t('inventaire.aucunInventaire')}
            aide={edit && tenantId ? t('inventaire.aucunInvAideCreer')
                  : tenantId ? t('inventaire.aucunInvEspace')
                  : t('inventaire.aucunInvPartage')} />
        )}

        {/* Ordre alphabetique stable (l'ordre d'arrivee des documents varie). */}
        {[...inventaires].sort((a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' })).map(inv => {
          const nbCat = aplatirArbre(inv.arbre).length;
          return (
            <LigneSwipe key={inv.id}
              enabled={edit}
              onSupprimer={() => supprimer(inv)}
              onEditer={() => ouvrirEdition(inv)}>
              <TouchableOpacity style={s.carte} activeOpacity={0.7}
                onPress={() => setOuvert(inv)}
                onLongPress={edit && Platform.OS === 'web' ? () => ouvrirEdition(inv) : undefined}
                delayLongPress={450}>
                <Text style={s.carteEmoji}>{inv.emoji || '📦'}</Text>
                <View style={{ flex: 1 }}>
                  <Text style={s.carteTitre} numberOfLines={1}>{inv.nom}</Text>
                  <Text style={s.carteSous}>
                    {nbCat ? t('inventaire.nbCategoriesN', { n: nbCat }) : t('inventaire.sansCategorie')}
                    {inv.proprietes?.length ? ` · ${t('inventaire.nProprietes', { n: inv.proprietes.length })}` : ''}
                  </Text>
                </View>
                <Text style={s.chev}>›</Text>
              </TouchableOpacity>
            </LigneSwipe>
          );
        })}

        {edit && charge && inventaires.length > 0 && (
          <Text style={s.astuce}>
            {Platform.OS === 'web'
              ? t('inventaire.astuceAppuiLong')
              : t('inventaire.astuceSwipe')}
          </Text>
        )}
        <View style={{ height: 24 }} />
      </ScrollView>

      {/* ── Feuille création / édition ────────────────────────────── */}
      <Feuille visible={!!sheet}
        titre={sheet?.id ? t('inventaire.modifierInventaire') : t('inventaire.nouvelInventaire')} emoji="📦"
        onFermer={() => setSheet(null)}
        action={{ icone: sheet?.id ? '💾' : '＋', onPress: enregistrer, disabled: !modifie || busy, label: t('commun.enregistrer') }}>
              <Text style={s.lbl}>{t('listes.nom')}</Text>
              <TextInput style={s.input} value={sheet?.nom || ''}
                onChangeText={(v) => setSheet(x => ({ ...x, nom: v }))}
                placeholder={sheet?.modele ? instancierModele(sheet.modele).nom : t('inventaire.phTitreInventaire')}
                placeholderTextColor={UI.faint} />

              {/* Les modèles ne s'appliquent qu'à la CRÉATION : ils posent l'arbre
                  de catégories initial, qu'on édite ensuite normalement. Placés
                  AVANT l'icône (le sélecteur d'emoji, défilant, masquait la suite). */}
              {!sheet?.id && (
                <>
                  <Text style={s.lbl}>{t('inventaire.partirModele')}</Text>
                  <TouchableOpacity style={[s.modele, !sheet?.modele && s.modeleOn]}
                    onPress={() => setSheet(x => ({ ...x, modele: null }))}>
                    <Text style={[s.modeleEmoji, { color: UI.accent, fontWeight: '600' }]}>＋</Text>
                    <View style={{ flex: 1 }}>
                      <Text style={s.modeleNom}>{t('inventaire.inventaireVide')}</Text>
                      <Text style={s.modeleDesc}>{t('inventaire.inventaireVideDesc')}</Text>
                    </View>
                  </TouchableOpacity>
                  {/* Mes modèles enregistrés (structure sauvegardée d'un inventaire). */}
                  {mesModeles.length > 0 && <Text style={s.modeleGroupe}>{t('inventaire.mesModeles')}</Text>}
                  {mesModeles.map(m => {
                    const sel = sheet?.modele && typeof sheet.modele === 'object' && sheet.modele.id === m.id;
                    return (
                      <TouchableOpacity key={m.id} style={[s.modele, sel && s.modeleOn]}
                        onPress={() => setSheet(x => ({ ...x, modele: m, emoji: x.nom.trim() ? x.emoji : (m.emoji || '📦') }))}
                        onLongPress={() => supprimerModele(m)} delayLongPress={450}>
                        <Text style={s.modeleEmoji}>{m.emoji || '📦'}</Text>
                        <View style={{ flex: 1 }}>
                          <Text style={s.modeleNom}>{m.nom}</Text>
                          <Text style={s.modeleDesc} numberOfLines={2}>{m.desc || t('inventaire.structureEnregistree')}</Text>
                          <Text style={s.modeleMeta}>{t('inventaire.modeleMeta', { c: nbCategoriesModele(m), p: m.proprietes?.length || 0 })} · {t('inventaire.appuiLongSupprimer')}</Text>
                        </View>
                      </TouchableOpacity>
                    );
                  })}

                  {mesModeles.length > 0 && <Text style={s.modeleGroupe}>{t('inventaire.modelesPrets')}</Text>}
                  {MODELES_INVENTAIRE.map(m => (
                    <TouchableOpacity key={m.id} style={[s.modele, sheet?.modele === m.id && s.modeleOn]}
                      onPress={() => setSheet(x => ({ ...x, modele: m.id, emoji: x.nom.trim() ? x.emoji : m.emoji }))}>
                      <Text style={s.modeleEmoji}>{m.emoji}</Text>
                      <View style={{ flex: 1 }}>
                        <Text style={s.modeleNom}>{labelTemplate(m, t)}</Text>
                        <Text style={s.modeleDesc} numberOfLines={2}>{descTemplate(m, t)}</Text>
                        <Text style={s.modeleMeta}>
                          {t('inventaire.modeleMeta', { c: nbCategoriesModele(m), p: m.proprietes?.length || 0 })}
                        </Text>
                      </View>
                    </TouchableOpacity>
                  ))}
                </>
              )}

              <Text style={s.lbl}>{t('profil.icone')}</Text>
              <SelecteurEmoji inline avecRecherche valeur={sheet?.emoji || '📦'}
                onChange={(e) => setSheet(x => ({ ...x, emoji: e }))} />
      </Feuille>
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  page:   { flex: 1, backgroundColor: UI.bg },
  corps:  { padding: 16 },
  hIc:    { fontSize: 19, color: UI.accent },
  espace: { fontSize: 12, color: UI.faint, fontWeight: '700', marginBottom: 12 },
  quota:  { fontSize: 12, color: UI.muted, lineHeight: 17, marginBottom: 12 },
  err:     { backgroundColor: UI.dangerTint, borderWidth: 1, borderColor: UI.danger, borderRadius: 10,
             padding: 11, margin: 12, marginBottom: 0 },
  errTxt:  { color: UI.danger, fontSize: 12.5, fontWeight: '700' },
  errAide: { color: UI.danger, fontSize: 11, marginTop: 3, opacity: 0.8 },
  // marginBottom géré par LigneSwipe (sinon le bouton de swipe déborde).
  carte:      { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: UI.card,
                borderRadius: 13, borderWidth: 1, borderColor: UI.border, padding: 14 },
  carteEmoji: { fontSize: 26 },
  carteTitre: { fontSize: 15.5, fontWeight: '700', color: UI.ink },
  carteSous:  { fontSize: 12.5, color: UI.muted, marginTop: 2 },
  chev:       { fontSize: 24, color: UI.faint, fontWeight: '300' },
  astuce:     { fontSize: 11.5, color: UI.faint, textAlign: 'center', marginTop: 14 },
  fond:    { flex: 1, backgroundColor: UI.overlay, justifyContent: 'flex-end' },
  feuille: { height: '82%', backgroundColor: UI.bg, borderTopLeftRadius: 18, borderTopRightRadius: 18, overflow: 'hidden' },
  feuilleCorps: { padding: 16 },
  lbl:    { fontSize: 12, fontWeight: '700', color: UI.muted, marginTop: 14, marginBottom: 7 },
  input:  { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 11,
            fontSize: 15, color: UI.ink, borderWidth: 0.5, borderColor: UI.champBord },
  modele:      { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: UI.card,
                 borderRadius: 12, borderWidth: 1, borderColor: UI.border, padding: 12, marginBottom: 8 },
  modeleOn:    { borderColor: UI.accent, backgroundColor: UI.accentTint },
  modeleEmoji: { fontSize: 24 },
  modeleNom:   { fontSize: 14.5, fontWeight: '700', color: UI.ink },
  modeleDesc:  { fontSize: 12.5, color: UI.muted, marginTop: 1 },
  modeleMeta:  { fontSize: 11, color: UI.faint, marginTop: 3 },
  modeleGroupe:{ fontSize: 11.5, fontWeight: '800', color: UI.faint, textTransform: 'uppercase',
                 letterSpacing: 0.5, marginTop: 8, marginBottom: 6 },
});
